import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { useTheme } from "../lib/theme";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Bell, BellOff, BellRing, Info, User, Mail, Shield, Palette, Languages, KeyRound, ChevronRight } from "lucide-react";
import { pushSupported, subscribePush, unsubscribePush, isSubscribed, sendTestPush, currentPermission } from "../lib/push";
import { toast } from "sonner";

export default function Settings() {
  const { t, lang, setLang } = useI18n();
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();
  const [supported] = useState(pushSupported());
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setPermission(await currentPermission());
    setSubscribed(await isSubscribed());
  };
  useEffect(() => { refresh(); }, []);

  const enable = async () => {
    setBusy(true);
    try {
      await subscribePush();
      toast.success("Notificações ativadas");
      refresh();
    } catch (e) {
      toast.error(e.message || "Falha ao ativar");
    } finally { setBusy(false); }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await unsubscribePush();
      toast.success("Notificações desativadas");
      refresh();
    } catch { toast.error("Falha"); } finally { setBusy(false); }
  };

  const test = async () => {
    try {
      const r = await sendTestPush();
      toast.success(`Enviadas: ${r.sent} · Falhas: ${r.failed}`);
    } catch { toast.error("Falha no teste"); }
  };

  const permBadge = {
    granted: { label: "Permitido", cls: "pill-success" },
    denied: { label: "Bloqueado no navegador", cls: "pill-danger" },
    default: { label: "Aguardando", cls: "pill-neutral" },
  }[permission] || { label: permission, cls: "pill-neutral" };

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="card-soft px-5 py-4">
        <div className="text-xs text-[var(--text-2)] font-medium">Perfil</div>
        <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">{t("nav_settings")}</h1>
      </div>

      {/* Profile card */}
      <div className="card-soft p-5 space-y-4">
        <div className="flex items-center gap-3 pb-3 border-b border-[var(--border-c)]">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[var(--brand)] to-[var(--brand-2)] flex items-center justify-center text-white text-xl font-bold">
            {(user?.name || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="font-display text-lg font-bold truncate">{user?.name}</div>
            <div className="text-xs font-mono text-[var(--text-2)] truncate">{user?.email}</div>
          </div>
          <span className="pill pill-info capitalize ml-auto">{user?.role}</span>
        </div>

        <InfoRow icon={<User size={14} />} label="Nome" value={user?.name} />
        <InfoRow icon={<Mail size={14} />} label="E-mail" value={user?.email} mono />
        <InfoRow icon={<Shield size={14} />} label="Papel" value={user?.role} capitalize />
      </div>

      <Link
        to="/security?tab=password"
        className="card-soft p-5 flex items-center gap-3 hover:border-[var(--brand)] transition-colors group"
        data-testid="settings-change-password"
      >
        <div className="w-11 h-11 rounded-2xl bg-[var(--brand-soft)] text-[var(--brand)] flex items-center justify-center shrink-0">
          <KeyRound size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base font-bold">Alterar senha</div>
          <p className="text-xs text-[var(--text-2)] mt-1">
            Troque sua senha de acesso. Recomendado no primeiro login.
          </p>
        </div>
        <ChevronRight size={18} className="text-[var(--text-3)] group-hover:text-[var(--brand)] shrink-0" />
      </Link>

      {/* Notifications */}
      <div className="card-soft p-5 space-y-4" data-testid="push-panel">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-11 h-11 rounded-2xl bg-[var(--brand-soft)] text-[var(--brand)] flex items-center justify-center shrink-0">
              {subscribed ? <BellRing size={18} /> : <Bell size={18} />}
            </div>
            <div className="min-w-0">
              <div className="font-display text-base font-bold">Notificações push</div>
              <p className="text-xs text-[var(--text-2)] mt-1 leading-relaxed">
                Receba alertas em tempo real quando uma nova entrega for atribuída, mesmo com o app fechado (Chrome/Edge/Firefox/Safari 16.4+).
              </p>
            </div>
          </div>
          <span className={`pill ${permBadge.cls} shrink-0`} data-testid="push-permission">{permBadge.label}</span>
        </div>

        {!supported && (
          <div className="rounded-2xl bg-[var(--warning-soft)] text-[var(--warning-text)] p-3 text-xs flex items-start gap-2">
            <Info size={14} className="mt-0.5 shrink-0" />
            <span>Este navegador não suporta Web Push. No iOS, instale o app na tela inicial e reabra pelo ícone.</span>
          </div>
        )}

        {supported && (
          <div className="flex flex-wrap gap-2 pt-1">
            {!subscribed ? (
              <Button onClick={enable} disabled={busy} className="rounded-xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] h-10 text-xs font-semibold px-4" data-testid="push-enable">
                <Bell size={12} className="mr-1" /> Ativar notificações
              </Button>
            ) : (
              <>
                <Button onClick={test} disabled={busy} className="rounded-xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] h-10 text-xs font-semibold px-4" data-testid="push-test">
                  Enviar teste
                </Button>
                <button onClick={disable} disabled={busy} className="inline-flex items-center gap-1.5 px-4 h-10 rounded-xl bg-[var(--surface-2)] hover:bg-[var(--danger-soft)] hover:text-[var(--danger)] text-xs font-semibold text-[var(--text-2)]" data-testid="push-disable">
                  <BellOff size={12} /> Desativar
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Preferences */}
      <div className="card-soft p-5 space-y-5">
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-xl bg-[var(--brand-soft)] text-[var(--brand)] flex items-center justify-center">
              <Languages size={16} />
            </div>
            <div className="font-display text-base font-bold">{t("language")}</div>
          </div>
          <div className="flex gap-2">
            {["pt", "en"].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-5 h-10 rounded-xl text-xs font-semibold transition-colors ${
                  lang === l
                    ? "bg-[var(--brand)] text-white shadow-sm"
                    : "bg-[var(--surface-2)] text-[var(--text-2)] hover:bg-[var(--brand-soft)] hover:text-[var(--brand)]"
                }`}
                data-testid={`lang-${l}`}
              >
                {l === "pt" ? "Português" : "English"}
              </button>
            ))}
          </div>
        </div>

        <div className="border-t border-[var(--border-c)] pt-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-xl bg-[var(--brand-soft)] text-[var(--brand)] flex items-center justify-center">
              <Palette size={16} />
            </div>
            <div className="font-display text-base font-bold">Tema</div>
          </div>
          <div className="flex gap-2">
            {["light", "dark"].map((th) => (
              <button
                key={th}
                onClick={() => setTheme(th)}
                className={`px-5 h-10 rounded-xl text-xs font-semibold transition-colors ${
                  theme === th
                    ? "bg-[var(--brand)] text-white shadow-sm"
                    : "bg-[var(--surface-2)] text-[var(--text-2)] hover:bg-[var(--brand-soft)] hover:text-[var(--brand)]"
                }`}
                data-testid={`theme-${th}`}
              >
                {th === "light" ? t("light") : t("dark")}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon, label, value, mono, capitalize }) {
  return (
    <div className="grid grid-cols-3 gap-3 items-center">
      <div className="flex items-center gap-2 text-xs text-[var(--text-2)] font-medium">
        <span className="text-[var(--text-3)]">{icon}</span>{label}
      </div>
      <div className={`col-span-2 text-sm text-[var(--text-1)] ${mono ? "font-mono" : "font-medium"} ${capitalize ? "capitalize" : ""}`}>
        {value}
      </div>
    </div>
  );
}
